import { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  chakra,
  Flex,
  Icon,
  Link,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  useToast
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useUser } from '@/utils/useUser';
import { postData } from '@/utils/helpers';
import { PLUS_MONTHLY_PLAN_ID, PLUS_YEARLY_PLAN_ID } from '@/utils/constants';
import { LogSnag } from 'logsnag';
import { LOGSNAG_API_TOKEN } from '@/utils/constants';

const Feature = (props) => {
  return (
    <Flex align="center">
      <Flex shrink={0}>
        <Icon
          boxSize={5}
          mt={1}
          mr={2}
          color={useColorModeValue('primary.500', 'primary.300')}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          ></path>
        </Icon>
      </Flex>
      <Box ml={4}>
        <chakra.span mt={2} color={useColorModeValue('gray.700', 'gray.400')}>
          {props.children}
        </chakra.span>
      </Box>
    </Flex>
  );
};

const Pricing = ({ trial, subscription }) => {
  const { user, session } = useUser();
  const router = useRouter();
  const [frequency, setFrequency] = useState('month');
  const toast = useToast();

  const handleTrial = async (planId) => {
    if (user) {
      const { data, error } = await postData({
        url: '/api/startTrial',
        token: session.access_token,
        data: { planId: planId }
      });
      // console.log('handleTrial', data);
      if (error) {
        toast({
          title: 'Error',
          description: 'An error occurred. Please contact support.',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
      } else {
        toast({
          title: 'Success!',
          description:
            'Your 7-day trial of FontVisual Plus plan has now started.',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
        const logsnag = new LogSnag(LOGSNAG_API_TOKEN);
        await logsnag.publish({
          project: 'fontvisual',
          channel: 'trial',
          event: 'New Customer Trial Started! 🔥',
          description: `email: ${user.email}`,
          icon: '💵',
          notify: true
        });

        router.push('/fonts');
      }
    } else {
      router.push({
        pathname: '/signup',
        query: { planId: planId, isTrial: true }
      });
    }
  };

  const handlePayment = (planId) => {
    if (user) {
      //TODO hackfix for mobileview below - frameStyle
      if (typeof window != 'undefined') {
        const Paddle = window.Paddle;
        Paddle.Checkout.open({
          product: planId,
          email: user.email,
          frameStyle:
            'z-index: 99999; display: block; background-color: transparent; border: 0px none transparent; overflow-x: hidden; overflow-y: auto; visibility: visible; margin: 0px; padding: 0px; -webkit-tap-highlight-color: transparent; position: fixed; left: 0px; top: 0px; width: 100%; height: 100%;',
          successCallback: (data, err) => {
            // console.log('successCallback', data);
            toast({
              title: 'Success!',
              description: 'Thank you for subscribing!',
              status: 'success',
              duration: 3000,
              isClosable: true
            });

            router.push('/fonts');

            //ADD YOUR EXTRA LOGIC TO SEND TO BACKEND
          }
        });
      }

      // if (typeof window !== 'undefined') {
      //   const Paddle = window.Paddle;
      //   Paddle.Checkout.open({
      //     produc: productId,
      //     email: user.email,
      //     successCallback: (data, err) => {
      //       console.log(data);

      //       //ADD YOUR EXTRA LOGIC TO SEND TO BACKEND
      //     }
      //   });
      // }
    } else {
      router.push({
        pathname: '/signup',
        query: { planId: planId, isTrial: false }
      });
    }
  };

  return (
    <>
      <Box
        w={{ base: 'sm', md: 'full' }}
        px="10"
        pb="10"
        // position={{ base: 'relative', md: 'absolute' }}
        //   bg={useColorModeValue('gray.100', 'gray.700')}
      >
        <Box
          w="full"
          px={{ base: [5, 5, 2, 2], md: [10, 10, 4, 4] }}
          mx="auto"
          textAlign="center"
        >
          <Text
            mb={2}
            fontSize="xx-large"
            fontWeight="bold"
            lineHeight="tight"
            color="primary.800"
          >
            Plans & Pricing
          </Text>
          <Text
            mb={6}
            fontSize={['lg', 'lg', 'lg', 'xl']}
            color={useColorModeValue('gray.600', 'gray.400')}
          >
            Plus plan start with a free 7-day trial · No credit card required ·
            Cancel at anytime
          </Text>
          {/* <Box
            // bg={useColorModeValue('primary.100', 'primary.200')}
            border="0.5px"
            borderRadius="md"
            width="fit-content"
            mx="auto"
            my="8"
            p="1"
          >
            <Text
              fontStyle="italic"
              fontSize={['sm', 'sm', 'sm', 'md']}
              color={useColorModeValue('primary.600', 'primary.400')}
            >
              These are our introductory prices. Hurry and avail now! Prices
              will increase soon.
            </Text>
          </Box> */}
          <Box d="inline-block" position="relative">
            <Flex justify="center" mx={['auto', 0]} mb={-2}>
              <Stack
                direction="row"
                justify="space-between"
                p="2"
                textAlign="center"
                rounded="md"
                bg={useColorModeValue('gray.200', 'gray.600')}
              >
                <Button
                  colorScheme="primary"
                  variant={frequency === 'month' ? 'solid' : 'ghost'}
                  onClick={() => setFrequency('month')}
                  px={6}
                >
                  Monthly
                </Button>
                <Button
                  colorScheme="primary"
                  variant={frequency === 'year' ? 'solid' : 'ghost'}
                  onClick={() => setFrequency('year')}
                  px={6}
                >
                  Yearly
                </Button>
              </Stack>
            </Flex>

            <Badge
              colorScheme="primary"
              borderRadius="full"
              textTransform="lowercase"
              p="2"
              height="fit-content"
              textColor="gray.500"
              ml={{ md: '2' }}
              position="absolute"
              top={{ base: 'calc(50% + 34px)', md: 'calc(50% - 12px)' }}
              right={{ base: '24px', md: '420px' }}
            >
              2 months free 🎁
            </Badge>
          </Box>
        </Box>
        <Box
          maxW={{ base: 'sm', md: 'fit-content' }}
          py="20"
          mx={{ base: 'auto', md: 'auto' }}
        >
          <SimpleGrid
            columns={!subscription && !trial ? [1, 1, 2, 2] : [1, 1, 1, 1]}
            gap={[16, 8]}
          >
            {!subscription && !trial ? (
              <Box
                px={{ md: '20' }}
                rounded={['none', 'lg']}
                shadow={['none', 'lg']}
                bg={useColorModeValue('white', 'gray.800')}
              >
                <Flex
                  direction="column"
                  justify="space-between"
                  p="6"
                  borderBottomWidth="1px"
                  borderColor={useColorModeValue('gray.200', 'gray.600')}
                >
                  <Text
                    mb={1}
                    fontSize="lg"
                    fontWeight="semibold"
                    color={useColorModeValue('gray.700', 'gray.400')}
                  >
                    FREE
                  </Text>
                  <Text
                    mb={2}
                    fontSize="5xl"
                    fontWeight={['bold', 'extrabold']}
                    color={useColorModeValue('gray.900', 'gray.50')}
                    lineHeight="tight"
                  >
                    ${frequency === 'month' ? 0 : 0}
                    <chakra.span
                      fontSize="2xl"
                      fontWeight="medium"
                      color={useColorModeValue('gray.600', 'gray.400')}
                    >
                      {' '}
                      /{frequency}
                    </chakra.span>
                  </Text>
                  <Button
                    w={['full', 'full', 'auto', 'auto']}
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    px={5}
                    py={3}
                    border="solid transparent"
                    fontWeight="bold"
                    rounded="full"
                    shadow="md"
                    colorScheme="primary"
                    //   color={useColorModeValue('white')}
                    //   bg={useColorModeValue('primary.600', 'primary.500')}
                    //   _hover={{
                    //     bg: useColorModeValue('primary.700', 'primary.600')
                    //   }}
                    onClick={() => {
                      user ? router.push('/fonts') : router.push('/signup');
                    }}
                  >
                    Get started
                  </Button>
                </Flex>
                <Stack direction="column" p="6" spacing="3" flexGrow="1">
                  <Feature>Up to 1 GB custom fonts</Feature>
                  <Feature>Up to 10 background images</Feature>
                  <Feature>
                    In-App Font Preview only (no external links)
                  </Feature>
                  <Feature>Use Google Fonts (1000+ free fonts)</Feature>
                </Stack>
              </Box>
            ) : null}

            {/* <Box
              rounded={['none', 'lg']}
              shadow={['none', 'lg']}
              bg={useColorModeValue('white', 'gray.800')}
            >
              <Flex
                direction="column"
                justify="space-between"
                p="6"
                borderBottomWidth="1px"
                borderColor={useColorModeValue('gray.200', 'gray.600')}
              >
                <Text
                  mb={1}
                  fontSize="lg"
                  fontWeight="semibold"
                  color={useColorModeValue('gray.700', 'gray.400')}
                >
                  STARTER
                </Text>
                <Text
                  mb={2}
                  fontSize="5xl"
                  fontWeight={['bold', 'extrabold']}
                  color={useColorModeValue('gray.900', 'gray.50')}
                  lineHeight="tight"
                >
                  ${frequency === 'month' ? 6 : 60}
                  <chakra.span
                    fontSize="2xl"
                    fontWeight="medium"
                    color={useColorModeValue('gray.600', 'gray.400')}
                  >
                    {' '}
                    /{frequency}
                  </chakra.span>
                </Text>
                <Button
                  w={['full', 'full', 'auto', 'auto']}
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  px={5}
                  py={3}
                  border="solid transparent"
                  fontWeight="bold"
                  rounded="full"
                  shadow="md"
                  colorScheme="primary"
                  //   color={useColorModeValue('gray.800')}
                  //   bg={useColorModeValue('gray.50', 'gray.600')}
                  //   _hover={{
                  //     bg: useColorModeValue('gray.100', 'gray.700')
                  //   }}
                  onClick={() => {
                    if (!subscription && !trial) {
                      handleTrial(
                        frequency === 'year'
                          ? STARTER_YEARLY_PLAN_ID
                          : STARTER_MONTHLY_PLAN_ID
                      );
                    } else {
                      handlePayment(
                        frequency === 'year'
                          ? STARTER_YEARLY_PLAN_ID
                          : STARTER_MONTHLY_PLAN_ID
                      );
                    }
                  }}
                >
                  {trial
                    ? frequency === 'month'
                      ? 'Pay $6 /month'
                      : 'Pay $60 /year'
                    : 'Start 7-day trial'}
                </Button>
              </Flex>
              <Stack direction="column" p="6" spacing="3" flexGrow="1">
                <Feature>Up to 25 GB custom fonts</Feature>
                <Feature>Up to 100 images</Feature>
                <Feature>Use Google Fonts (1000+ free fonts)</Feature>
                <Feature>Filter fonts (coming soon)</Feature>
                <Feature>Organize fonts (coming soon)</Feature>
                <Feature>Export As Image(coming soon)</Feature>
                <Feature>... more features coming soon!</Feature>
              </Stack>
            </Box> */}

            <Box
              px={{ md: '20' }}
              rounded={['none', 'lg']}
              shadow={['none', 'lg']}
              bg={useColorModeValue('white', 'gray.800')}
            >
              <Flex
                direction="column"
                justify="space-between"
                p="6"
                borderBottomWidth="1px"
                borderColor={useColorModeValue('gray.200', 'gray.600')}
              >
                <Text
                  mb={1}
                  fontSize="lg"
                  fontWeight="semibold"
                  color={useColorModeValue('gray.700', 'gray.400')}
                >
                  PLUS
                </Text>
                <Text
                  mb={2}
                  fontSize="5xl"
                  fontWeight={['bold', 'extrabold']}
                  color={useColorModeValue('gray.900', 'gray.50')}
                  lineHeight="tight"
                >
                  ${frequency === 'month' ? 9.99 : 99}
                  <chakra.span
                    fontSize="2xl"
                    fontWeight="medium"
                    color={useColorModeValue('gray.600', 'gray.400')}
                  >
                    {' '}
                    /{frequency}
                  </chakra.span>
                </Text>
                <Button
                  w={['full', 'full', 'auto', 'auto']}
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  px={5}
                  py={3}
                  border="solid transparent"
                  fontWeight="bold"
                  rounded="full"
                  shadow="md"
                  colorScheme="primary"
                  //   color={useColorModeValue('gray.800')}
                  //   bg={useColorModeValue('gray.50', 'gray.600')}
                  //   _hover={{
                  //     bg: useColorModeValue('gray.100', 'gray.700')
                  //   }}
                  onClick={() => {
                    if (!subscription && !trial) {
                      handleTrial(
                        frequency === 'year'
                          ? PLUS_YEARLY_PLAN_ID
                          : PLUS_MONTHLY_PLAN_ID
                      );
                    } else {
                      handlePayment(
                        frequency === 'year'
                          ? PLUS_YEARLY_PLAN_ID
                          : PLUS_MONTHLY_PLAN_ID
                      );
                    }
                  }}
                >
                  {trial
                    ? frequency === 'month'
                      ? 'Pay $9.99 /month'
                      : 'Pay $99 /year'
                    : 'Start 7-day trial'}
                </Button>
              </Flex>
              <Stack direction="column" p="6" spacing="3" flexGrow="1">
                <Feature>
                  Up to <strong>50 GB custom fonts</strong>
                </Feature>
                <Feature>
                  Up to <strong>1000 background images</strong>
                </Feature>
                <Feature>
                  <strong>Unlimited Showcase (font preview) pages</strong>
                </Feature>
                <Feature>
                  <strong>Embed font previews</strong>
                </Feature>
                <Feature>
                  <strong>Shareable font preview links</strong>
                </Feature>
                <Feature>Use Google Fonts (1000+ free fonts)</Feature>

                <Feature>... more features coming soon!</Feature>
              </Stack>
            </Box>
          </SimpleGrid>
        </Box>

        <Box
          d="flex"
          flexDirection="column"
          fontSize="md"
          color="gray.500"
          mx={{ base: '10', md: 'auto' }}
          maxW={{ base: 'fit-content', md: '5xl' }}
        >
          <Text
            // fontSize="sm"
            fontStyle={'italic'}
            // color={useColorModeValue('gray.900', 'gray.50')}
            // textAlign="center"
          >
            * Price in USD
          </Text>
          <Text mt="4">
            VAT may be applied to your total depending on your local digital VAT
            laws. If you own a business, make sure to add your VAT number in the
            form, which should remove any VAT.
          </Text>
          <Text mt="4">
            If you have any questions, please email{' '}
            <Link href="mailto:support@fontvisual.com" color="primary.500">
              support@fontvisual.com
            </Link>
            .
          </Text>
        </Box>
      </Box>
    </>
  );
};

export default Pricing;
